import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import useNotification from "../../components/notifications/useNotifications";
import AwaitingDismantlingPanel from "../../components/productionPlanner/AwaitingDismantlingPanel";
import BayDropZone from "../../components/productionPlanner/BayDropZone";
import PlannerSearchSummary from "../../components/productionPlanner/PlannerSearchSummary";
import PlannerToolbar from "../../components/productionPlanner/PlannerToolbar";
import ProductionLineDayColumn from "../../components/productionPlanner/ProductionLineDayColumn";
import RebookModal from "../../components/productionPlanner/RebookModal";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useDismantlingBays from "../../data/useDismantlingBays";
import useDismantlingBaySetting from "../../data/useDismantlingBaySetting";
import useProductionPlanner, { usePlannerVehicleLookup } from "../../data/useProductionPlanner";
import useApiHelper from "../../hooks/useApiHelper";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import { useLabelGenerator } from "../../hooks/useLabelGenerator";
import { DismantlingBayType, IDismantlingBay, IDismantlingBaySetting } from "../../models/DismantlingBay";
import {
  IPlannerDragItem,
  IPlannerSearchResult,
  IPlannerVehicleRow,
  PlannerColourBy,
  PlannerComplexityFilter,
  PlannerDayRange,
  PlannerSort,
  PlannerView,
} from "../../models/ProductionPlanner";
import { FinancialDecision, ISingleVehicleResponse } from "../../models/Vehicle";
import { PLANNER_SEARCH_MIN_LENGTH, plannerSearchMatches } from "../../utils/plannerSearch";
import printHtml from "../../utils/printHtml";

const allocationDateKey = (iso: string | null): string | null => {
  if (!iso) return null;
  return DateTime.fromISO(iso, { zone: "utc" }).toISODate();
};

const STORAGE_PREFIX = "productionPlanner";
const STORAGE_KEY = {
  view: `${STORAGE_PREFIX}.view`,
  sort: `${STORAGE_PREFIX}.sort`,
  complexityFilter: `${STORAGE_PREFIX}.complexityFilter`,
  colourBy: `${STORAGE_PREFIX}.colourBy`,
  dayRange: `${STORAGE_PREFIX}.dayRange`,
};

const readStored = <T extends string>(key: string, allowed: readonly T[], fallback: T): T => {
  if (typeof window === "undefined") return fallback;
  const value = window.localStorage.getItem(key);
  return value !== null && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
};

const readStoredDayRange = (fallback: PlannerDayRange): PlannerDayRange => {
  if (typeof window === "undefined") return fallback;
  const value = window.localStorage.getItem(STORAGE_KEY.dayRange);
  const allowed: PlannerDayRange[] = [1, 3, 5, 7];
  const parsed = value !== null ? Number(value) : NaN;
  return allowed.includes(parsed as PlannerDayRange) ? (parsed as PlannerDayRange) : fallback;
};

const writeStored = (key: string, value: string | number) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(key, String(value));
};

export default function ProductionPlanner() {
  const { t } = useTranslation("production");
  const { setInfo } = usePageNameContext();
  const isTme = CheckFeatureFlag("TME");
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers(t("Allocation"));
  const { generateVehicleLabel } = useLabelGenerator();
  const { addNotification } = useNotification();
  const { get } = useApiHelper();

  const [view, setView] = useState<PlannerView>(() =>
    readStored<PlannerView>(STORAGE_KEY.view, ["Bays", "ProductionLine"] as const, "ProductionLine")
  );
  const [sort, setSort] = useState<PlannerSort>(() =>
    readStored<PlannerSort>(
      STORAGE_KEY.sort,
      ["Oldest", "Newest", "Easiest", "Hardest"] as const,
      "Oldest"
    )
  );
  const [complexityFilter, setComplexityFilter] = useState<PlannerComplexityFilter>(() =>
    readStored<PlannerComplexityFilter>(
      STORAGE_KEY.complexityFilter,
      ["All", "Easy", "Medium", "Hard"] as const,
      "All"
    )
  );
  const [colourBy, setColourBy] = useState<PlannerColourBy>(() =>
    readStored<PlannerColourBy>(STORAGE_KEY.colourBy, ["Complexity", "Age"] as const, "Complexity")
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [dayRange, setDayRange] = useState<PlannerDayRange>(() => readStoredDayRange(3));
  const [selectedDate, setSelectedDate] = useState<DateTime>(DateTime.local().startOf("day"));
  const [clearOpen, setClearOpen] = useState<boolean>(false);
  const [isClearingAll, setIsClearingAll] = useState<boolean>(false);
  const [isPrintingLabels, setIsPrintingLabels] = useState<boolean>(false);
  const [rebookOpen, setRebookOpen] = useState<boolean>(false);
  const [isRebooking, setIsRebooking] = useState<boolean>(false);

  const fromDate = selectedDate;
  const toDate = view === "Bays" ? selectedDate : selectedDate.plus({ days: dayRange - 1 });
  const fromIso = fromDate.toISODate() ?? "";
  const toIso = toDate.toISODate() ?? "";

  const { dismantlingBays } = useDismantlingBays();
  const { awaiting, allocated, unfinished, allocateVehicle, clearAllocation, clearDay, reorderBay } =
    useProductionPlanner({
      from: fromIso,
      to: toIso,
      unfinishedEnabled: rebookOpen,
    });
  const { settingsRange } = useDismantlingBaySetting({ from: fromIso, to: toIso });

  useEffect(() => {
    setInfo({
      name: t("Production Planner"),
      desc: t("Plan the dismantling production line by allocating vehicles across the coming days"),
    });
  }, [setInfo, t]);

  useEffect(() => writeStored(STORAGE_KEY.view, view), [view]);
  useEffect(() => writeStored(STORAGE_KEY.sort, sort), [sort]);
  useEffect(() => writeStored(STORAGE_KEY.complexityFilter, complexityFilter), [complexityFilter]);
  useEffect(() => writeStored(STORAGE_KEY.colourBy, colourBy), [colourBy]);
  useEffect(() => writeStored(STORAGE_KEY.dayRange, dayRange), [dayRange]);

  const bays: IDismantlingBay[] = useMemo(() => {
    return [...(dismantlingBays.data?.data ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  }, [dismantlingBays.data]);

  const bayViewBays = useMemo(
    () => bays.filter((b) => b.type === DismantlingBayType.Bay),
    [bays]
  );

  const productionLineBays = useMemo(
    () => bays.filter((b) => b.type === DismantlingBayType.ProductionLine),
    [bays]
  );

  const allocatedRows: IPlannerVehicleRow[] = useMemo(
    () => allocated.data?.data ?? [],
    [allocated.data]
  );
  // With TME enabled, only vehicles marked Purchased are awaiting dismantling.
  const awaitingRows: IPlannerVehicleRow[] = useMemo(() => {
    const rows = awaiting.data?.data ?? [];
    return isTme ? rows.filter((v) => v.financialDecision === FinancialDecision.Purchased) : rows;
  }, [awaiting.data, isTme]);

  const allocatedByBayDate = useMemo(() => {
    const map = new Map<string, IPlannerVehicleRow[]>();
    allocatedRows.forEach((v) => {
      const dateKey = allocationDateKey(v.plannedDismantlingOn);
      if (!v.dismantlingBayId || !dateKey) return;
      const key = `${v.dismantlingBayId}|${dateKey}`;
      const list = map.get(key) ?? [];
      list.push(v);
      map.set(key, list);
    });
    return map;
  }, [allocatedRows]);

  const vehiclesFor = (bayId: string, dateIso: string): IPlannerVehicleRow[] =>
    allocatedByBayDate.get(`${bayId}|${dateIso}`) ?? [];

  const settingsByBayDate = useMemo(() => {
    const map = new Map<string, IDismantlingBaySetting>();
    (settingsRange.data?.data ?? []).forEach((s) => {
      const dateKey = allocationDateKey(s.date);
      if (!dateKey) return;
      map.set(`${s.dismantlingBayId}|${dateKey}`, s);
    });
    return map;
  }, [settingsRange.data]);

  const settingFor = (bayId: string, dateIso: string): IDismantlingBaySetting | undefined =>
    settingsByBayDate.get(`${bayId}|${dateIso}`);

  const handleDrop = (item: IPlannerDragItem, bayId: string, dateIso: string) => {
    if (item.currentBayId === bayId && allocationDateKey(item.currentPlannedOn) === dateIso) return;

    allocateVehicle.mutate(
      {
        vehicleId: item.vehicleId,
        body: { dismantlingBayId: bayId, plannedOn: dateIso },
      },
      {
        onSuccess: saveHandlers.onSuccess,
        onError: saveHandlers.onError,
      }
    );
  };

  const handleReorder = (bayId: string, dateIso: string, vehicleIds: string[]) => {
    reorderBay.mutate(
      { dismantlingBayId: bayId, plannedOn: dateIso, vehicleIds },
      {
        onSuccess: saveHandlers.onSuccess,
        onError: saveHandlers.onError,
      }
    );
  };

  const handleDropToAwaiting = (item: IPlannerDragItem) => {
    if (!item.currentBayId) return;

    clearAllocation.mutate(item.vehicleId, {
      onSuccess: deleteHandlers.onSuccess,
      onError: deleteHandlers.onError,
    });
  };

  const handlePrintAll = async () => {
    if (isPrintingLabels) return;

    const visibleBayIds = new Set(
      (view === "Bays" ? bayViewBays : productionLineBays).map((b) => b.id)
    );
    const printableVehicles = allocatedRows.filter(
      (v) => v.dismantlingBayId && visibleBayIds.has(v.dismantlingBayId)
    );

    if (printableVehicles.length === 0) {
      addNotification({
        variant: "warn",
        primaryText: t("No vehicles allocated in the current view"),
      });
      return;
    }

    setIsPrintingLabels(true);
    try {
      const htmlPages = await Promise.all(
        printableVehicles.map(async (row) => {
          const response = await get<ISingleVehicleResponse>(`/vehicle/${row.id}`);
          return generateVehicleLabel(response.data);
        })
      );

      const allHtml = htmlPages.join("<div style='page-break-after: always;'></div>");
      printHtml(allHtml);
    } catch {
      addNotification({
        variant: "error",
        primaryText: t("Unable to print vehicle labels"),
      });
    } finally {
      setIsPrintingLabels(false);
    }
  };

  const handleRebook = async (
    vehicleIds: string[],
    dismantlingBayId: string,
    plannedOn: string
  ) => {
    if (isRebooking) return;

    setIsRebooking(true);
    try {
      await Promise.all(
        vehicleIds.map((vehicleId) =>
          allocateVehicle.mutateAsync({
            vehicleId,
            body: { dismantlingBayId, plannedOn },
          })
        )
      );
      saveHandlers.onSuccess();
      setRebookOpen(false);
    } catch {
      saveHandlers.onError();
    } finally {
      setIsRebooking(false);
    }
  };

  const handleClearConfirm = async () => {
    const type = view === "Bays" ? DismantlingBayType.Bay : DismantlingBayType.ProductionLine;
    setIsClearingAll(true);
    try {
      await Promise.all(
        dayColumns.map((date) => clearDay.mutateAsync({ date, dismantlingBayType: type }))
      );
      deleteHandlers.onSuccess();
      setClearOpen(false);
    } catch {
      deleteHandlers.onError();
    } finally {
      setIsClearingAll(false);
    }
  };

  const dayColumns: string[] = useMemo(() => {
    if (view === "Bays") return [fromIso];
    return Array.from({ length: dayRange }, (_, i) =>
      selectedDate.plus({ days: i }).toISODate() ?? ""
    );
  }, [view, dayRange, fromIso, selectedDate]);

  const bayNameById = useMemo(() => new Map(bays.map((b) => [b.id, b.name])), [bays]);

  const trimmedSearch = searchTerm.trim();
  const isSearching = trimmedSearch.length > 0;

  // What the screen is actually showing right now: the visible bays across the
  // visible days. A match outside this is a match the user can't see.
  const visibleAllocatedRows = useMemo(() => {
    const visibleBayIds = new Set(
      (view === "Bays" ? bayViewBays : productionLineBays).map((b) => b.id)
    );
    const visibleDays = new Set(dayColumns);

    return allocatedRows.filter((v) => {
      const dateKey = allocationDateKey(v.plannedDismantlingOn);
      return (
        !!v.dismantlingBayId &&
        visibleBayIds.has(v.dismantlingBayId) &&
        !!dateKey &&
        visibleDays.has(dateKey)
      );
    });
  }, [allocatedRows, view, bayViewBays, productionLineBays, dayColumns]);

  const plannerMatches = useMemo(
    () =>
      isSearching
        ? visibleAllocatedRows.filter((v) => plannerSearchMatches(v, searchTerm))
        : [],
    [isSearching, visibleAllocatedRows, searchTerm]
  );

  const plannerMatchIds = useMemo(
    () => new Set(plannerMatches.map((v) => v.id)),
    [plannerMatches]
  );

  const awaitingMatchCount = useMemo(
    () =>
      isSearching
        ? awaitingRows.filter((v) => plannerSearchMatches(v, searchTerm)).length
        : 0,
    [isSearching, awaitingRows, searchTerm]
  );

  // Nothing on screen or in the awaiting list accounts for the search, so ask the
  // server where the vehicle actually is — most usefully, the day it's booked in.
  const lookupEnabled =
    trimmedSearch.length >= PLANNER_SEARCH_MIN_LENGTH &&
    plannerMatches.length === 0 &&
    awaitingMatchCount === 0;

  const { lookup, isLookingUp } = usePlannerVehicleLookup(searchTerm, lookupEnabled);
  const lookupResults: IPlannerSearchResult[] = lookupEnabled
    ? (lookup.data?.data ?? [])
    : [];

  const focusVehicle = (vehicleId: string) => {
    document
      .querySelector(`[data-planner-vehicle="${vehicleId}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
  };

  // Bring the first hit into view as soon as a search lands on one. Keyed on the id
  // so the 30s background refetch doesn't keep yanking the planner around.
  const firstMatchId = plannerMatches[0]?.id ?? null;
  useEffect(() => {
    if (!firstMatchId) return;
    focusVehicle(firstMatchId);
  }, [firstMatchId]);

  const handleGoToResult = (result: IPlannerSearchResult) => {
    // The booking may well be in the other view as well as another day.
    if (result.bayType === DismantlingBayType.Bay || result.bayType === DismantlingBayType.ProductionLine) {
      setView(result.bayType === DismantlingBayType.Bay ? "Bays" : "ProductionLine");
    }

    if (!result.plannedDismantlingOn) return;

    const iso = DateTime.fromISO(result.plannedDismantlingOn, { zone: "utc" }).toISODate();
    if (!iso) return;

    setSelectedDate(DateTime.fromISO(iso).startOf("day"));
  };

  const isLoading = dismantlingBays.isLoading || awaiting.isLoading || allocated.isLoading;

  return (
    <>
      <ConfirmModal
        open={clearOpen}
        setOpen={setClearOpen}
        title={t("Clear allocations")}
        message={t(
          view === "Bays"
            ? dayColumns.length > 1
              ? "Remove every bay allocation for {{from}} - {{to}}? This cannot be undone."
              : "Remove every bay allocation for {{from}}? This cannot be undone."
            : dayColumns.length > 1
              ? "Remove every production line allocation for {{from}} - {{to}}? This cannot be undone."
              : "Remove every production line allocation for {{from}}? This cannot be undone.",
          { from: fromDate.toFormat("ccc d LLL"), to: toDate.toFormat("ccc d LLL") }
        )}
        confirmButtonText={t("Clear")}
        onConfirm={handleClearConfirm}
        isLoading={isClearingAll}
      />

      <RebookModal
        open={rebookOpen}
        setOpen={setRebookOpen}
        vehicles={unfinished.data?.data ?? []}
        isLoading={unfinished.isLoading}
        productionLineBays={productionLineBays}
        onConfirm={handleRebook}
        isRebooking={isRebooking}
      />

      <div className="mt-2 flex flex-col gap-2 h-[calc(100vh-9rem)]">
        <PlannerToolbar
          view={view}
          setView={setView}
          sort={sort}
          setSort={setSort}
          complexityFilter={complexityFilter}
          setComplexityFilter={setComplexityFilter}
          colourBy={colourBy}
          setColourBy={setColourBy}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          dayRange={dayRange}
          setDayRange={setDayRange}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          bayCount={view === "Bays" ? bayViewBays.length : productionLineBays.length}
          onRebook={() => setRebookOpen(true)}
          onPrintAll={handlePrintAll}
          isPrintingLabels={isPrintingLabels}
          onClearAll={() => setClearOpen(true)}
          isClearing={isClearingAll}
        />

        <PlannerSearchSummary
          searchTerm={searchTerm}
          plannerMatches={plannerMatches}
          bayNameById={bayNameById}
          awaitingMatchCount={awaitingMatchCount}
          canLookup={trimmedSearch.length >= PLANNER_SEARCH_MIN_LENGTH}
          isLookingUp={isLookingUp}
          lookupResults={lookupResults}
          onFocusVehicle={focusVehicle}
          onGoToResult={handleGoToResult}
          onClear={() => setSearchTerm("")}
        />

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center bg-white border border-gray-200 rounded-md">
            <LoadingWheel />
          </div>
        ) : (
          <div className="flex-1 grid grid-cols-12 gap-2 min-h-0">
            <div className="col-span-12 lg:col-span-3 xl:col-span-2 min-h-0">
              <AwaitingDismantlingPanel
                vehicles={awaitingRows}
                isLoading={awaiting.isLoading}
                searchTerm={searchTerm}
                complexityFilter={complexityFilter}
                sort={sort}
                colourBy={colourBy}
                onDropFromBay={handleDropToAwaiting}
              />
            </div>

            <div className="col-span-12 lg:col-span-9 xl:col-span-10 min-h-0">
              <div className="h-full overflow-x-auto overflow-y-hidden">
                <div className="flex gap-2 h-full pr-2">
                  {view === "Bays" ? (
                    bayViewBays.length === 0 ? (
                      <div className="flex-1 bg-white border border-gray-200 rounded-md flex items-center justify-center text-sm text-gray-500">
                        {t("No dismantling bays configured. Add some in Admin Settings.")}
                      </div>
                    ) : (
                      bayViewBays.map((bay) => (
                        <BayDropZone
                          key={bay.id}
                          bay={bay}
                          date={fromIso}
                          vehicles={vehiclesFor(bay.id, fromIso)}
                          colourBy={colourBy}
                          onDrop={handleDrop}
                          onReorder={handleReorder}
                          variant="column"
                          setting={settingFor(bay.id, fromIso)}
                          searchMatchIds={plannerMatchIds}
                          isSearchActive={isSearching && plannerMatchIds.size > 0}
                        />
                      ))
                    )
                  ) : (
                    dayColumns.map((dateIso) => (
                      <ProductionLineDayColumn
                        key={dateIso}
                        date={dateIso}
                        productionLineBays={productionLineBays}
                        vehicles={allocatedRows.filter(
                          (v) => allocationDateKey(v.plannedDismantlingOn) === dateIso
                        )}
                        colourBy={colourBy}
                        onDrop={handleDrop}
                        onReorder={handleReorder}
                        settingFor={(bayId) => settingFor(bayId, dateIso)}
                        searchMatchIds={plannerMatchIds}
                        isSearchActive={isSearching && plannerMatchIds.size > 0}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
